import { clamp01 } from "../../shared/easing";
import type { RenderModel } from "../renderModel";
import {
  compactCount,
  mFont,
  roundedRectPath,
} from "../renderConstants";

// ── Extracted screen function ───────────────────────────────────────

export function drawSidePanelTelemetryScreen(
  ctx: CanvasRenderingContext2D,
  width: number, height: number,
  side: "left" | "right",
  model: RenderModel
): void {
  if (width < 50) return;

  const run = model.run;
  const padX = 4;
  const padTop = 8;
  const cardW = width - padX * 2;
  const cardH = Math.min(height * 0.55, 260);
  const cardX = padX;
  const cardY = padTop;

  // Card background
  ctx.save();
  roundedRectPath(ctx, cardX, cardY, cardW, cardH, 6);
  ctx.fillStyle = "rgba(4,8,16,0.75)";
  ctx.fill();
  roundedRectPath(ctx, cardX, cardY, cardW, cardH, 6);
  ctx.strokeStyle = "rgba(100,140,200,0.35)";
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  const cx = cardX + cardW / 2;
  let y = cardY + 8;

  if (side === "left") {
    // Header: "YOU"
    ctx.fillStyle = "#8ac4ff";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("YOU", cx, y);
    y += 10;

    // Subheader: W/L placeholder
    ctx.fillStyle = "#7a9abc";
    ctx.font = mFont("bold", 4);
    ctx.fillText("W/L --/--", cx, y);
    y += 8;

    // Ping placeholder
    ctx.fillStyle = "#44cc66";
    ctx.beginPath();
    ctx.arc(cx - 14, y + 3, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#7a9abc";
    ctx.font = mFont("bold", 4);
    ctx.fillText("--ms", cx + 4, y);
    y += 10;

    // Separator
    ctx.strokeStyle = "rgba(100,140,200,0.2)";
    ctx.beginPath();
    ctx.moveTo(cardX + 4, y);
    ctx.lineTo(cardX + cardW - 4, y);
    ctx.stroke();
    y += 6;

    // Stat rows
    const stats: { label: string; value: string; color: string }[] = [];
    const score = run != null ? compactCount(run.totalScore) : "--";
    const left = model.pointsToNextLevel != null ? compactCount(Math.max(0, Math.floor(model.pointsToNextLevel))) : "--";
    const doomSec = model.doomClockSec;
    const doomMax = model.doomClockMaxSec;
    const doomStr = doomSec != null && doomSec > 0 ? `${doomSec.toFixed(1)}s` : "--";
    const grooveMult = run != null ? `x${run.groove.multiplier}` : "--";

    stats.push({ label: "SCORE", value: score, color: "#ffffff" });
    stats.push({ label: "LEFT", value: left, color: "#66ff88" });
    stats.push({ label: "DOOM", value: doomStr, color: "#ff6666" });
    stats.push({ label: "GROOVE", value: grooveMult, color: "#cc88ff" });

    for (const stat of stats) {
      ctx.font = mFont("bold", 4);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#7a9abc";
      ctx.fillText(stat.label, cardX + 6, y);
      ctx.textAlign = "right";
      ctx.fillStyle = stat.color;
      ctx.fillText(stat.value, cardX + cardW - 6, y);

      // Mini doom progress bar
      if (stat.label === "DOOM" && doomMax > 0 && doomSec > 0) {
        y += 7;
        const barW = cardW - 12;
        const barH = 3;
        const fill = clamp01(doomSec / doomMax);
        ctx.fillStyle = "rgba(255,255,255,0.08)";
        ctx.fillRect(cardX + 6, y, barW, barH);
        const barColor = fill > 0.5 ? "#44cc66" : fill > 0.25 ? "#ffaa22" : "#ff4444";
        ctx.fillStyle = barColor;
        ctx.fillRect(cardX + 6, y, barW * fill, barH);
      }

      y += 9;
    }
  } else {
    // Header: "OPPONENT"
    ctx.fillStyle = "#ff9a9a";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("OPPONENT", cx, y);
    y += 10;

    // Subheader
    ctx.fillStyle = "#7a9abc";
    ctx.font = mFont("bold", 4);
    ctx.fillText("W/L --/--", cx, y);
    y += 8;

    // Ping placeholder
    ctx.fillStyle = "#666666";
    ctx.beginPath();
    ctx.arc(cx - 14, y + 3, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = mFont("bold", 4);
    ctx.fillText("--ms", cx + 4, y);
    y += 10;

    // Separator
    ctx.strokeStyle = "rgba(100,140,200,0.2)";
    ctx.beginPath();
    ctx.moveTo(cardX + 4, y);
    ctx.lineTo(cardX + cardW - 4, y);
    ctx.stroke();
    y += 6;

    // Placeholder stat rows
    const labels = ["SCORE", "LEFT", "DOOM", "GROOVE"];
    for (const label of labels) {
      ctx.font = mFont("bold", 4);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#555555";
      ctx.fillText(label, cardX + 6, y);
      ctx.textAlign = "right";
      ctx.fillStyle = "#666666";
      ctx.fillText("--", cardX + cardW - 6, y);
      y += 9;
    }
  }
}
